import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API, authFetch, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import DocumentUploader from "@/components/DocumentUploader";
import PetPassport from "@/components/PetPassport";
import MedicalExams from "@/components/MedicalExams";
import LegalOwnerSection from "@/components/LegalOwnerSection";
import Reminders from "@/components/Reminders";
import VaccinesGrouped from "@/components/VaccinesGrouped";
import DocumentsSection from "@/components/DocumentsSection";
import { PetProfileHeader, PetIdentityCard } from "@/components/PetProfileHeader";
import { VaccineStatusCard, ExamsStatusCard, DocumentsStatusCard } from "@/components/HealthStatusCards";
import { 
  ArrowLeft, ChevronRight, PawPrint, Syringe, FileText, Calendar,
  Plus, Camera, Share2, Download, Edit, User, Shield, 
  Bell, CreditCard, Stethoscope, Settings, Info, FolderOpen,
  Copy, CheckCircle, AlertTriangle, X, Mail, Loader2
} from "lucide-react";

// Download PDF with authentication
const downloadPdfDossier = async (petId, petName) => {
  try {
    toast.info("Generazione PDF in corso...");
    const response = await authFetch(`${API}/dossier/pdf/${petId}`);
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Errore nel download");
    }
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Dossier_${petName || 'Pet'}_${new Date().toISOString().split('T')[0]}.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
    toast.success("PDF scaricato!");
  } catch (error) {
    console.error("PDF download error:", error);
    toast.error(error.message || "Errore nel download del PDF");
  }
};

export default function FascicoloApple() {
  const navigate = useNavigate();
  const { petId } = useParams();
  const { user } = useAuth();
  const [pet, setPet] = useState(null);
  const [vaccines, setVaccines] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(null); // null = dashboard, 'vaccines' | 'exams' | 'documents'
  const [showDocUploader, setShowDocUploader] = useState(false);
  const [showAddVaccine, setShowAddVaccine] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showEditPet, setShowEditPet] = useState(false);
  const [showIDCard, setShowIDCard] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(window.innerWidth < 1024);
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    
    // Job completion listener
    const handleJobComplete = (event) => {
      const job = event.detail;
      if (job.pet_id === petId) {
        fetchPetData();
        toast.success("Dati aggiornati dal documento elaborato!");
      }
    };
    window.addEventListener('job-completed', handleJobComplete);
    
    if (petId) fetchPetData();
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('job-completed', handleJobComplete);
    };
  }, [petId]);

  const fetchPetData = async () => {
    try {
      const [petRes, vaccinesRes, documentsRes] = await Promise.all([
        authFetch(`${API}/pets/${petId}`),
        authFetch(`${API}/vaccines?pet_id=${petId}`),
        authFetch(`${API}/documents?pet_id=${petId}`)
      ]);
      
      if (petRes.ok) setPet(await petRes.json());
      if (vaccinesRes.ok) setVaccines(await vaccinesRes.json());
      if (documentsRes.ok) setDocuments(await documentsRes.json());
      
    } catch (error) {
      console.error('Error:', error);
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVaccine = async (vaccineId) => {
    if (!confirm("Eliminare questo vaccino?")) return;
    try {
      const res = await authFetch(`${API}/vaccines/${vaccineId}`, { method: 'DELETE' });
      if (res.ok) {
        setVaccines(vaccines.filter(v => v.vaccine_id !== vaccineId));
        toast.success("Vaccino eliminato");
      }
    } catch (error) {
      toast.error("Errore");
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!confirm("Eliminare questo documento?")) return;
    try {
      const res = await authFetch(`${API}/documents/${docId}`, { method: 'DELETE' });
      if (res.ok) {
        setDocuments(documents.filter(d => d.document_id !== docId));
        toast.success("Documento eliminato");
      }
    } catch (error) {
      toast.error("Errore");
    }
  };

  const handleDeletePet = async () => {
    if (!confirm(`Sei sicuro di voler eliminare ${pet.name}? Questa azione è irreversibile.`)) return;
    try {
      const res = await authFetch(`${API}/pets/${petId}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success(`${pet.name} eliminato`);
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error("Errore");
    }
  };

  const handleUpdatePet = async (updatedData, useFullReplace = false) => {
    try {
      const method = useFullReplace ? 'PUT' : 'PATCH';
      let dataToSend = useFullReplace 
        ? { name: pet.name, species: pet.species, ...updatedData }
        : updatedData;
      
      const res = await authFetch(`${API}/pets/${petId}`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSend)
      });
      
      if (res.ok) {
        setPet(await res.json());
        toast.success("Modifiche salvate!");
        setShowEditPet(false);
      } else {
        const error = await res.json().catch(() => ({}));
        toast.error(error.detail || "Errore nel salvataggio");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F2F2F7] dark:bg-black">
        <PawPrint className="text-[#FF6B4A] animate-pulse" size={48} />
      </div>
    );
  }

  // Pet not found
  if (!pet) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#F2F2F7] dark:bg-black p-6">
        <PawPrint className="text-[#C7C7CC] mb-4" size={64} />
        <h2 className="text-xl font-bold text-[#1D1D1F] dark:text-white mb-2">Animale non trovato</h2>
        <Button onClick={() => navigate('/dashboard')} className="mt-4 rounded-xl">
          Torna alla home
        </Button>
      </div>
    );
  }

  // Section detail views
  if (activeSection === 'vaccines') {
    return (
      <div className="min-h-screen bg-[#F2F2F7] dark:bg-black pb-32">
        <div className="bg-white dark:bg-[#1C1C1E] sticky top-0 z-40 border-b border-black/5 dark:border-white/10">
          <div className="px-4 py-3 flex items-center gap-3">
            <button 
              onClick={() => setActiveSection(null)}
              className="w-10 h-10 rounded-xl bg-[#F5F5F7] dark:bg-[#2C2C2E] flex items-center justify-center"
            >
              <ArrowLeft size={20} className="text-[#1D1D1F] dark:text-white" />
            </button>
            <h1 className="text-lg font-bold text-[#1D1D1F] dark:text-white">Vaccini</h1>
          </div>
        </div>
        <div className="p-4">
          <VaccinesGrouped 
            petId={petId}
            petName={pet?.name}
            onAddVaccine={() => setShowAddVaccine(true)}
            onDeleteVaccine={handleDeleteVaccine}
          />
        </div>
        <AddVaccineDialog 
          petId={pet.pet_id}
          open={showAddVaccine}
          onClose={() => setShowAddVaccine(false)}
          onSuccess={() => { setShowAddVaccine(false); fetchPetData(); }}
        />
      </div>
    );
  }

  if (activeSection === 'exams') {
    return (
      <div className="min-h-screen bg-[#F2F2F7] dark:bg-black pb-32">
        <div className="bg-white dark:bg-[#1C1C1E] sticky top-0 z-40 border-b border-black/5 dark:border-white/10">
          <div className="px-4 py-3 flex items-center gap-3">
            <button 
              onClick={() => setActiveSection(null)}
              className="w-10 h-10 rounded-xl bg-[#F5F5F7] dark:bg-[#2C2C2E] flex items-center justify-center"
            >
              <ArrowLeft size={20} className="text-[#1D1D1F] dark:text-white" />
            </button>
            <h1 className="text-lg font-bold text-[#1D1D1F] dark:text-white">Esami</h1>
          </div>
        </div>
        <div className="p-4">
          <MedicalExams petId={pet.pet_id} petName={pet.name} />
        </div>
      </div>
    );
  }

  if (activeSection === 'documents') {
    return (
      <div className="min-h-screen bg-[#F2F2F7] dark:bg-black pb-32">
        <div className="bg-white dark:bg-[#1C1C1E] sticky top-0 z-40 border-b border-black/5 dark:border-white/10">
          <div className="px-4 py-3 flex items-center gap-3">
            <button 
              onClick={() => setActiveSection(null)}
              className="w-10 h-10 rounded-xl bg-[#F5F5F7] dark:bg-[#2C2C2E] flex items-center justify-center"
            >
              <ArrowLeft size={20} className="text-[#1D1D1F] dark:text-white" />
            </button>
            <h1 className="text-lg font-bold text-[#1D1D1F] dark:text-white">Documenti</h1>
          </div>
        </div>
        <div className="p-4">
          <DocumentsSection 
            documents={documents}
            onUpload={() => setShowDocUploader(true)}
            onDelete={handleDeleteDocument}
            onRefresh={fetchPetData}
          />
        </div>
        <DocumentUploader 
          petId={pet.pet_id}
          petName={pet.name}
          open={showDocUploader}
          onClose={() => setShowDocUploader(false)}
          onSuccess={() => { setShowDocUploader(false); fetchPetData(); }}
        />
      </div>
    );
  }

  // Main Dashboard View - MOBILE
  if (isMobile) {
    return (
      <div className="min-h-screen bg-[#F2F2F7] dark:bg-black pb-32" data-testid="fascicolo-apple">
        {/* Header */}
        <div className="bg-white dark:bg-[#1C1C1E] border-b border-black/5 dark:border-white/10">
          <PetProfileHeader 
            pet={pet}
            compact={false}
            onBack={() => navigate(-1)}
            onEdit={() => setShowEditPet(true)}
            onShare={() => setShowShare(true)}
          />
        </div>

        <div className="p-4 space-y-5">
          {/* Health Status Cards */}
          <section>
            <h2 className="text-sm font-semibold text-[#8E8E93] uppercase tracking-wider mb-3 px-1">
              Stato Salute
            </h2>
            <div className="space-y-3">
              <VaccineStatusCard 
                petId={petId} 
                vaccines={vaccines} 
                onClick={() => setActiveSection('vaccines')} 
              />
              <div className="grid grid-cols-2 gap-3">
                <ExamsStatusCard 
                  petId={petId} 
                  onClick={() => setActiveSection('exams')} 
                />
                <DocumentsStatusCard 
                  documents={documents} 
                  onClick={() => setActiveSection('documents')} 
                />
              </div>
            </div>
          </section>

          {/* Quick Actions */}
          <section>
            <h2 className="text-sm font-semibold text-[#8E8E93] uppercase tracking-wider mb-3 px-1">
              Azioni Rapide
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => setShowIDCard(true)}
                className="h-14 bg-[#007AFF] hover:bg-[#0066CC] text-white rounded-2xl font-medium"
                data-testid="view-id-card-btn"
              >
                <CreditCard size={18} className="mr-2" />
                ID Digitale
              </Button>
              <Button
                onClick={() => setShowDocUploader(true)}
                variant="outline"
                className="h-14 rounded-2xl font-medium border-[#E5E5EA] dark:border-[#3A3A3C]"
                data-testid="upload-doc-btn"
              >
                <Camera size={18} className="mr-2" />
                Carica
              </Button>
            </div>
            <Button
              onClick={() => downloadPdfDossier(pet.pet_id, pet.name)}
              variant="outline"
              className="w-full h-12 mt-3 rounded-2xl font-medium border-[#FF6B4A] text-[#FF6B4A] hover:bg-[#FF6B4A] hover:text-white"
              data-testid="download-pdf-btn"
            >
              <Download size={18} className="mr-2" />
              Scarica Dossier PDF
            </Button>
          </section>

          {/* iOS-style Grouped Lists (Accordion) */}
          <section>
            <Accordion type="single" collapsible className="space-y-3">
              {/* Profile Section */}
              <AccordionItem 
                value="profile" 
                className="bg-white dark:bg-[#1C1C1E] rounded-2xl border-0 shadow-sm overflow-hidden"
              >
                <AccordionTrigger className="px-4 py-4 hover:no-underline hover:bg-[#F5F5F7] dark:hover:bg-[#2C2C2E]">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                      <Info size={16} className="text-blue-500" />
                    </div>
                    <span className="font-semibold text-[#1D1D1F] dark:text-white">Anagrafica</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <div className="space-y-3 pt-2">
                    <InfoRow label="Nome" value={pet.name} />
                    <InfoRow label="Specie" value={pet.species === 'dog' ? 'Cane' : pet.species === 'cat' ? 'Gatto' : pet.species} />
                    <InfoRow label="Razza" value={pet.breed || '—'} />
                    <InfoRow label="Data di nascita" value={pet.birth_date ? new Date(pet.birth_date).toLocaleDateString('it-IT') : '—'} />
                    <InfoRow label="Sesso" value={pet.sex === 'M' ? 'Maschio' : pet.sex === 'F' ? 'Femmina' : '—'} />
                    <InfoRow label="Colore" value={pet.color || '—'} />
                    <InfoRow label="Peso" value={pet.weight ? `${pet.weight} kg` : '—'} />
                    {pet.microchip && <InfoRow label="Microchip" value={pet.microchip} mono />}
                    {pet.eu_passport_number && <InfoRow label="Passaporto UE" value={pet.eu_passport_number} mono />}
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Legal Owner Section */}
              <AccordionItem 
                value="owner" 
                className="bg-white dark:bg-[#1C1C1E] rounded-2xl border-0 shadow-sm overflow-hidden"
              >
                <AccordionTrigger className="px-4 py-4 hover:no-underline hover:bg-[#F5F5F7] dark:hover:bg-[#2C2C2E]">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                      <User size={16} className="text-emerald-500" />
                    </div>
                    <span className="font-semibold text-[#1D1D1F] dark:text-white">Proprietario Legale</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4 pt-2">
                  <LegalOwnerSection pet={pet} onUpdate={handleUpdatePet} compact />
                </AccordionContent>
              </AccordionItem>

              {/* Reminders Section */}
              <AccordionItem 
                value="reminders" 
                className="bg-white dark:bg-[#1C1C1E] rounded-2xl border-0 shadow-sm overflow-hidden"
              >
                <AccordionTrigger className="px-4 py-4 hover:no-underline hover:bg-[#F5F5F7] dark:hover:bg-[#2C2C2E]">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                      <Bell size={16} className="text-amber-500" />
                    </div>
                    <span className="font-semibold text-[#1D1D1F] dark:text-white">Promemoria</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4 pt-2">
                  <Reminders petId={petId} petName={pet?.name} compact />
                </AccordionContent>
              </AccordionItem>

              {/* Settings Section */}
              <AccordionItem 
                value="settings" 
                className="bg-white dark:bg-[#1C1C1E] rounded-2xl border-0 shadow-sm overflow-hidden"
              >
                <AccordionTrigger className="px-4 py-4 hover:no-underline hover:bg-[#F5F5F7] dark:hover:bg-[#2C2C2E]">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-slate-500/10 flex items-center justify-center">
                      <Settings size={16} className="text-slate-500" />
                    </div>
                    <span className="font-semibold text-[#1D1D1F] dark:text-white">Impostazioni</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4 pt-2 space-y-2">
                  <button 
                    onClick={() => setShowEditPet(true)}
                    className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-[#F5F5F7] dark:hover:bg-[#2C2C2E] transition-colors"
                  >
                    <span className="text-[#1D1D1F] dark:text-white">Modifica profilo</span>
                    <ChevronRight size={16} className="text-[#C7C7CC]" />
                  </button>
                  <button 
                    onClick={() => setShowShare(true)}
                    className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-[#F5F5F7] dark:hover:bg-[#2C2C2E] transition-colors"
                  >
                    <span className="text-[#1D1D1F] dark:text-white">Condividi fascicolo</span>
                    <ChevronRight size={16} className="text-[#C7C7CC]" />
                  </button>
                  <button 
                    onClick={handleDeletePet}
                    className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors text-red-500"
                  >
                    <span>Elimina {pet.name}</span>
                    <ChevronRight size={16} />
                  </button>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </section>
        </div>

        {/* Dialogs */}
        <ShareDialog pet={pet} open={showShare} onClose={() => setShowShare(false)} />
        <DocumentUploader petId={pet.pet_id} petName={pet.name} open={showDocUploader} onClose={() => setShowDocUploader(false)} onSuccess={() => { setShowDocUploader(false); fetchPetData(); }} />
        <AddVaccineDialog petId={pet.pet_id} open={showAddVaccine} onClose={() => setShowAddVaccine(false)} onSuccess={() => { setShowAddVaccine(false); fetchPetData(); }} />
        <EditPetDialog pet={pet} open={showEditPet} onClose={() => setShowEditPet(false)} onSave={handleUpdatePet} onDelete={handleDeletePet} />
        <PetPassport pet={pet} owner={user} vaccines={vaccines} open={showIDCard} onClose={() => setShowIDCard(false)} />
      </div>
    );
  }

  // Desktop Layout
  return (
    <div className="min-h-screen bg-[#F2F2F7] dark:bg-black" data-testid="fascicolo-apple-desktop">
      <div className="max-w-7xl mx-auto p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/dashboard')}
              className="w-10 h-10 rounded-xl bg-white dark:bg-[#1C1C1E] border border-black/5 dark:border-white/10 flex items-center justify-center hover:bg-[#F5F5F7] dark:hover:bg-[#2C2C2E] transition-colors"
            >
              <ArrowLeft size={20} className="text-[#1D1D1F] dark:text-white" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-[#1D1D1F] dark:text-white tracking-tight">
                {pet.name}
              </h1>
              <p className="text-[#8E8E93]">{pet.breed || pet.species} • Fascicolo digitale</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowShare(true)} className="rounded-xl">
              <Share2 size={18} className="mr-2" /> Condividi
            </Button>
            <Button onClick={() => setShowDocUploader(true)} className="bg-[#FF6B4A] hover:bg-[#E05A3A] rounded-xl">
              <Camera size={18} className="mr-2" /> Carica documento
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Left Sidebar */}
          <div className="col-span-4 space-y-6">
            <PetIdentityCard pet={pet} onEdit={() => setShowEditPet(true)} />
            
            {/* Quick Actions */}
            <div className="space-y-3">
              <Button
                onClick={() => setShowIDCard(true)}
                className="w-full h-11 bg-[#007AFF] hover:bg-[#0066CC] text-white rounded-xl font-medium"
              >
                <CreditCard size={18} className="mr-2" />
                Documento di Identità
              </Button>
              <Button
                onClick={() => downloadPdfDossier(pet.pet_id, pet.name)}
                variant="outline"
                className="w-full h-11 rounded-xl font-medium border-[#FF6B4A] text-[#FF6B4A] hover:bg-[#FF6B4A] hover:text-white"
              >
                <Download size={18} className="mr-2" />
                Scarica Dossier PDF
              </Button>
            </div>

            {/* Legal Owner */}
            <div className="bg-white dark:bg-[#1C1C1E] rounded-3xl p-5 shadow-sm border border-black/5 dark:border-white/10">
              <LegalOwnerSection pet={pet} onUpdate={handleUpdatePet} />
            </div>
          </div>

          {/* Main Content */}
          <div className="col-span-8 space-y-6">
            {/* Health Status Cards */}
            <div className="grid grid-cols-3 gap-4">
              <VaccineStatusCard 
                petId={petId} 
                vaccines={vaccines} 
                onClick={() => setActiveSection('vaccines')} 
              />
              <ExamsStatusCard 
                petId={petId} 
                onClick={() => setActiveSection('exams')} 
              />
              <DocumentsStatusCard 
                documents={documents} 
                onClick={() => setActiveSection('documents')} 
              />
            </div>

            {/* Vaccines Section */}
            <div className="bg-white dark:bg-[#1C1C1E] rounded-3xl p-6 shadow-sm border border-black/5 dark:border-white/10">
              <VaccinesGrouped 
                petId={petId}
                petName={pet?.name}
                onAddVaccine={() => setShowAddVaccine(true)}
                onDeleteVaccine={handleDeleteVaccine}
              />
            </div>

            {/* Reminders Section */}
            <div className="bg-white dark:bg-[#1C1C1E] rounded-3xl p-6 shadow-sm border border-black/5 dark:border-white/10">
              <Reminders petId={petId} petName={pet?.name} />
            </div>
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <ShareDialog pet={pet} open={showShare} onClose={() => setShowShare(false)} />
      <DocumentUploader petId={pet.pet_id} petName={pet.name} open={showDocUploader} onClose={() => setShowDocUploader(false)} onSuccess={() => { setShowDocUploader(false); fetchPetData(); }} />
      <AddVaccineDialog petId={pet.pet_id} open={showAddVaccine} onClose={() => setShowAddVaccine(false)} onSuccess={() => { setShowAddVaccine(false); fetchPetData(); }} />
      <EditPetDialog pet={pet} open={showEditPet} onClose={() => setShowEditPet(false)} onSave={handleUpdatePet} onDelete={handleDeletePet} />
      <PetPassport pet={pet} owner={user} vaccines={vaccines} open={showIDCard} onClose={() => setShowIDCard(false)} />
    </div>
  );
}

// Info Row for Accordion content
function InfoRow({ label, value, mono = false }) {
  return (
    <div className="flex justify-between items-center py-2 border-b border-[#E5E5EA] dark:border-[#3A3A3C] last:border-0">
      <span className="text-[#8E8E93] text-sm">{label}</span>
      <span className={`text-[#1D1D1F] dark:text-white text-sm font-medium ${mono ? 'font-mono tracking-wider' : ''}`}>
        {value}
      </span>
    </div>
  );
}

// Share Dialog
function ShareDialog({ pet, open, onClose }) {
  const [shareLink, setShareLink] = useState("");
  const [copied, setCopied] = useState(false);
  
  useEffect(() => {
    if (pet) {
      setShareLink(`${window.location.origin}/shared/${pet.share_token || pet.pet_id}`);
    }
  }, [pet]);
  
  const copyLink = () => {
    navigator.clipboard.writeText(shareLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  if (!pet) return null;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[420px] rounded-3xl bg-white dark:bg-[#1C1C1E] p-0 border-0">
        <div className="p-6">
          <DialogHeader className="mb-4">
            <DialogTitle className="text-xl font-bold text-[#1D1D1F] dark:text-white flex items-center gap-3">
              <Share2 className="text-[#007AFF]" size={24} />
              Condividi {pet.name}
            </DialogTitle>
          </DialogHeader>
          
          <p className="text-[#8E8E93] mb-4 text-sm">
            Genera un link per condividere il fascicolo.
          </p>
          
          <div className="bg-[#F5F5F7] dark:bg-[#2C2C2E] rounded-2xl p-4 mb-4">
            <div className="flex gap-2">
              <input 
                type="text" 
                value={shareLink} 
                readOnly 
                className="flex-1 bg-white dark:bg-[#1C1C1E] rounded-xl px-3 py-2 text-sm font-mono text-[#1D1D1F] dark:text-white border-0"
              />
              <Button onClick={copyLink} variant="outline" className="rounded-xl">
                {copied ? <CheckCircle size={18} className="text-emerald-500" /> : <Copy size={18} />}
              </Button>
            </div>
          </div>
          
          <div className="bg-amber-50 dark:bg-amber-500/10 rounded-2xl p-3 flex items-start gap-3">
            <AlertTriangle className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" size={18} />
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Chi riceve questo link potrà vedere i dati sanitari.
            </p>
          </div>
          
          <Button onClick={onClose} variant="outline" className="w-full mt-4 rounded-xl">
            Chiudi
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Add Vaccine Dialog
function AddVaccineDialog({ petId, open, onClose, onSuccess }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [vaccine, setVaccine] = useState({
    vaccine_name: "",
    date_administered: "",
    expiry_date: "",
    vet_name: "",
    batch_number: ""
  });
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const payload = {
        pet_id: petId,
        name: vaccine.vaccine_name,
        date_administered: vaccine.date_administered,
        expiry_date: vaccine.expiry_date || null,
        vet_name: vaccine.vet_name || null,
        batch_number: vaccine.batch_number || null,
        data_source: 'self'
      };
      
      const response = await authFetch(`${API}/vaccines`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        toast.success("Vaccino aggiunto!");
        setVaccine({ vaccine_name: "", date_administered: "", expiry_date: "", vet_name: "", batch_number: "" });
        onSuccess?.();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Errore");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const commonVaccines = ["Antirabbica", "Cimurro", "Parvovirosi", "Epatite", "Leptospirosi", "Parainfluenza", "Bordetella"];
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[420px] rounded-3xl bg-white dark:bg-[#1C1C1E] p-0 border-0">
        <div className="p-6">
          <DialogHeader className="mb-5">
            <DialogTitle className="text-xl font-bold text-[#1D1D1F] dark:text-white">Aggiungi Vaccino</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-sm font-medium mb-2 block text-[#1D1D1F] dark:text-white">Nome vaccino *</Label>
              <Select value={vaccine.vaccine_name} onValueChange={(v) => setVaccine({...vaccine, vaccine_name: v})}>
                <SelectTrigger className="h-12 rounded-xl bg-[#F5F5F7] dark:bg-[#2C2C2E] border-0">
                  <SelectValue placeholder="Seleziona..." />
                </SelectTrigger>
                <SelectContent>
                  {commonVaccines.map(v => (
                    <SelectItem key={v} value={v}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium mb-2 block text-[#1D1D1F] dark:text-white">Data *</Label>
                <Input
                  type="date"
                  value={vaccine.date_administered}
                  onChange={(e) => setVaccine({...vaccine, date_administered: e.target.value})}
                  className="h-12 rounded-xl bg-[#F5F5F7] dark:bg-[#2C2C2E] border-0"
                  required
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-2 block text-[#1D1D1F] dark:text-white">Scadenza</Label>
                <Input
                  type="date"
                  value={vaccine.expiry_date}
                  onChange={(e) => setVaccine({...vaccine, expiry_date: e.target.value})}
                  className="h-12 rounded-xl bg-[#F5F5F7] dark:bg-[#2C2C2E] border-0"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-sm font-medium mb-2 block text-[#1D1D1F] dark:text-white">Veterinario</Label>
              <Input
                value={vaccine.vet_name}
                onChange={(e) => setVaccine({...vaccine, vet_name: e.target.value})}
                placeholder="Nome del veterinario"
                className="h-12 rounded-xl bg-[#F5F5F7] dark:bg-[#2C2C2E] border-0"
              />
            </div>
            
            <Button type="submit" disabled={isSubmitting || !vaccine.vaccine_name || !vaccine.date_administered} className="w-full h-12 bg-[#007AFF] hover:bg-[#0066CC] rounded-xl">
              {isSubmitting ? "Aggiunta..." : "Aggiungi vaccino"}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Edit Pet Dialog
function EditPetDialog({ pet, open, onClose, onSave, onDelete }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "", species: "dog", breed: "", birth_date: "", weight: "",
    microchip: "", color: "", sex: "", photo_url: "", eu_passport_number: ""
  });

  useEffect(() => {
    if (pet) {
      setFormData({
        name: pet.name || "",
        species: pet.species || "dog",
        breed: pet.breed || "",
        birth_date: pet.birth_date || "",
        weight: pet.weight || "",
        microchip: pet.microchip || "",
        color: pet.color || "",
        sex: pet.sex || "",
        photo_url: pet.photo_url || "",
        eu_passport_number: pet.eu_passport_number || ""
      });
    }
  }, [pet]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const dataToSave = { ...formData, weight: formData.weight ? parseFloat(formData.weight) : null };
    await onSave(dataToSave, true);
    setIsSubmitting(false);
  };

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingPhoto(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append("file", file);
      
      const response = await authFetch(`${API}/upload/pet-photo/${pet.pet_id}`, {
        method: "POST",
        body: uploadFormData
      });
      
      if (response.ok) {
        const data = await response.json();
        setFormData({ ...formData, photo_url: data.photo_url });
        toast.success("Foto caricata!");
      }
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setUploadingPhoto(false);
    }
    e.target.value = "";
  };

  if (!pet) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[500px] rounded-3xl bg-white dark:bg-[#1C1C1E] p-0 border-0 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <DialogHeader className="mb-5">
            <DialogTitle className="text-xl font-bold text-[#1D1D1F] dark:text-white">
              Modifica {pet.name}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Photo */}
            <div className="flex items-center gap-4 p-4 bg-[#F5F5F7] dark:bg-[#2C2C2E] rounded-2xl">
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoSelect} className="hidden" />
              <div 
                className="w-20 h-20 rounded-2xl overflow-hidden bg-white dark:bg-[#1C1C1E] flex items-center justify-center cursor-pointer"
                onClick={() => !uploadingPhoto && fileInputRef.current?.click()}
              >
                {formData.photo_url ? (
                  <img src={formData.photo_url} alt={formData.name} className="w-full h-full object-cover" />
                ) : (
                  <Camera className="text-[#C7C7CC]" size={28} />
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-[#1D1D1F] dark:text-white">Foto profilo</p>
                <p className="text-xs text-[#8E8E93]">Clicca per cambiare</p>
              </div>
            </div>
            
            <div>
              <Label className="text-sm font-medium mb-2 block">Nome *</Label>
              <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="h-12 rounded-xl bg-[#F5F5F7] dark:bg-[#2C2C2E] border-0" required />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium mb-2 block">Specie *</Label>
                <Select value={formData.species} onValueChange={(v) => setFormData({...formData, species: v})}>
                  <SelectTrigger className="h-12 rounded-xl bg-[#F5F5F7] dark:bg-[#2C2C2E] border-0"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dog">Cane</SelectItem>
                    <SelectItem value="cat">Gatto</SelectItem>
                    <SelectItem value="bird">Uccello</SelectItem>
                    <SelectItem value="rabbit">Coniglio</SelectItem>
                    <SelectItem value="horse">Cavallo</SelectItem>
                    <SelectItem value="other">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium mb-2 block">Sesso</Label>
                <Select value={formData.sex} onValueChange={(v) => setFormData({...formData, sex: v})}>
                  <SelectTrigger className="h-12 rounded-xl bg-[#F5F5F7] dark:bg-[#2C2C2E] border-0"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Maschio</SelectItem>
                    <SelectItem value="F">Femmina</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label className="text-sm font-medium mb-2 block">Razza</Label>
              <Input value={formData.breed} onChange={(e) => setFormData({...formData, breed: e.target.value})} className="h-12 rounded-xl bg-[#F5F5F7] dark:bg-[#2C2C2E] border-0" />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium mb-2 block">Data nascita</Label>
                <Input type="date" value={formData.birth_date} onChange={(e) => setFormData({...formData, birth_date: e.target.value})} className="h-12 rounded-xl bg-[#F5F5F7] dark:bg-[#2C2C2E] border-0" />
              </div>
              <div>
                <Label className="text-sm font-medium mb-2 block">Peso (kg)</Label>
                <Input type="number" step="0.1" value={formData.weight} onChange={(e) => setFormData({...formData, weight: e.target.value})} className="h-12 rounded-xl bg-[#F5F5F7] dark:bg-[#2C2C2E] border-0" />
              </div>
            </div>
            
            <div>
              <Label className="text-sm font-medium mb-2 block">Colore</Label>
              <Input value={formData.color} onChange={(e) => setFormData({...formData, color: e.target.value})} className="h-12 rounded-xl bg-[#F5F5F7] dark:bg-[#2C2C2E] border-0" />
            </div>
            
            <div>
              <Label className="text-sm font-medium mb-2 block">Microchip</Label>
              <Input value={formData.microchip} onChange={(e) => setFormData({...formData, microchip: e.target.value})} className="h-12 rounded-xl bg-[#F5F5F7] dark:bg-[#2C2C2E] border-0 font-mono" />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onDelete} className="flex-1 h-12 rounded-xl text-red-500 border-red-200 hover:bg-red-50">
                Elimina
              </Button>
              <Button type="submit" disabled={isSubmitting} className="flex-1 h-12 bg-[#007AFF] hover:bg-[#0066CC] rounded-xl">
                {isSubmitting ? "Salvataggio..." : "Salva"}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
